import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import styled from "styled-components";
import FormContainer, {
  SelectField,
  InputField,
  SubmitButton,
} from "components/form";
import { usePostAdminRepaymentScheduleMutation } from "store/admin/api/apiSlice";
import { Heading } from "components/ui/Text";
import { ApiAdminUser } from "utils/api/admin/user";
import {
  ApiAdminRepaymentScheduleRequest,
  RepaymentScheduleType,
} from "utils/api/admin/repaymentSchedule";
import { useFormContext } from "react-hook-form";
import Modal, { ModalApi } from "./Modal";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  text-align: left;
`;

interface Props {
  user: ApiAdminUser;
  onSuccess: () => void;
}

interface FormValues {
  scheduleType: RepaymentScheduleType;
  recurringDayOfWeek?: string;
  recurringDayOfMonth?: number;
  recurringWeekOfMonth?: string;
  previousBiWeeklyPayDate?: string;
}

const SCHEDULE_TYPE_OPTIONS = [
  { name: "Weekly", value: RepaymentScheduleType.weekly },
  { name: "Bi-Weekly", value: RepaymentScheduleType.biWeekly },
  {
    name: "Monthly (Exact Day)",
    value: RepaymentScheduleType.monthlyExactDay,
  },
  {
    name: "Monthly (Nth Week)",
    value: RepaymentScheduleType.monthlyNthWeek,
  },
];

const DAY_OF_WEEK_OPTIONS = [
  { name: "Monday", value: "1" },
  { name: "Tuesday", value: "2" },
  { name: "Wednesday", value: "3" },
  { name: "Thursday", value: "4" },
  { name: "Friday", value: "5" },
  { name: "Saturday", value: "6" },
  { name: "Sunday", value: "7" },
];

const WEEK_OF_MONTH_OPTIONS = [
  { name: "First", value: "1" },
  { name: "Second", value: "2" },
  { name: "Third", value: "3" },
  { name: "Fourth", value: "4" },
  { name: "Fifth", value: "5" },
];

const ConditionalFields = () => {
  const { watch } = useFormContext<FormValues>();
  const scheduleType = watch("scheduleType");

  return (
    <>
      {["weekly", "bi_weekly", "monthly_nth_week"].includes(scheduleType) && (
        <SelectField
          name="recurringDayOfWeek"
          label="Day of Week"
          options={DAY_OF_WEEK_OPTIONS}
          required
        />
      )}

      {scheduleType === "monthly_exact_day" && (
        <InputField
          name="recurringDayOfMonth"
          label="Day of Month"
          type="number"
          min={1}
          max={31}
          required
        />
      )}

      {scheduleType === "monthly_nth_week" && (
        <SelectField
          name="recurringWeekOfMonth"
          label="Week of Month"
          options={WEEK_OF_MONTH_OPTIONS}
          required
        />
      )}

      {scheduleType === "bi_weekly" && (
        <InputField
          name="previousBiWeeklyPayDate"
          label="Previous Bi-Weekly Pay Date"
          type="date"
          required
        />
      )}
    </>
  );
};

const CreateRepaymentScheduleModal = (
  { user, onSuccess }: Props,
  forwardedRef: React.ForwardedRef<unknown>
) => {
  const ref = useRef<ModalApi>(null);
  useImperativeHandle(forwardedRef, () => ref.current);
  const [postRepaymentSchedule, postRepaymentScheduleContext] =
    usePostAdminRepaymentScheduleMutation();

  const onSubmit = (values: FormValues) => {
    const params: ApiAdminRepaymentScheduleRequest = {
      userId: user.id,
      scheduleType: values.scheduleType,
      recurringDayOfWeek: values.recurringDayOfWeek
        ? parseInt(values.recurringDayOfWeek, 10)
        : undefined,
      recurringDayOfMonth: values.recurringDayOfMonth,
      recurringWeekOfMonth: values.recurringWeekOfMonth
        ? parseInt(values.recurringWeekOfMonth, 10)
        : undefined,
      previousBiWeeklyPayDate: values.previousBiWeeklyPayDate,
      active: true,
    };

    postRepaymentSchedule(params);
  };

  useEffect(() => {
    if (postRepaymentScheduleContext.isSuccess && onSuccess) {
      onSuccess();
      ref.current?.dismiss();
    }
  }, [postRepaymentScheduleContext.isSuccess, onSuccess]);

  return (
    <Modal ref={ref}>
      <Wrapper>
        <Heading.H4 tag="h2">Create Repayment Schedule</Heading.H4>

        <FormContainer
          onSubmit={onSubmit}
          defaultValues={{ scheduleType: SCHEDULE_TYPE_OPTIONS[0].value }}
        >
          <SelectField
            name="scheduleType"
            label="Schedule Type"
            options={SCHEDULE_TYPE_OPTIONS}
            required
          />

          <ConditionalFields />

          <SubmitButton>Create Schedule</SubmitButton>
        </FormContainer>
      </Wrapper>
    </Modal>
  );
};

export default forwardRef(CreateRepaymentScheduleModal);
